import {
  BottomButtonsLayout,
  Button,
  Headline3,
  HorizontalRadioList,
  HorizontalRadioListData,
} from 'aimo-ui';
import VehicleFilterItem from 'components/account/vehicle/VehicleFilterItem';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useParkingHistoryFilter, {
  ParkingHistoryFilters,
  ParkingTypeFilter,
  PaymentFilter,
} from 'hooks/useParkingHistoryFilter';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMapStore } from 'stores/mapStore';
import styled from 'styled-components/native';
import { ColumnWithMargins, StyledWrapper } from 'styles/ContainerStyles';
import { Vehicle } from 'types/generatedSchemaTypes';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

const Headline3Styled = styled(Headline3)`
  margin-bottom: 20px;
`;

const ParkingHistoryFilterModalContent = () => {
  const { DEFAULT_OVERFLOW } = HorizontalRadioList;
  const {
    filter,
    clearFilter,
    setFilter,
    parkingTypesFilterOptions,
    paymentFilterOptions,
  } = useParkingHistoryFilter();
  const [filterUiState, setFilterUiState] =
    useState<ParkingHistoryFilters>(filter);
  const { t } = useTranslation();
  const {
    flags: { payment_type_filter },
  } = useContext(FeatureFlagsContext);
  const showPaymentTypeFilter = showFeatureFlag(payment_type_filter);
  const showParkingTypeSelection = useMapStore(
    (state) => state.parkingTypesVisible
  );
  const {
    customer: { vehicles },
  } = useContext(AccountContext);

  const handleOnSelectedPayment = (
    newValue: HorizontalRadioListData<PaymentFilter>
  ) => {
    setFilterUiState({ ...filterUiState, payment: newValue.value });
  };

  const handleOnSelectedParkingType = (
    newValue: HorizontalRadioListData<ParkingTypeFilter>
  ) => {
    setFilterUiState({ ...filterUiState, parkingType: newValue.value });
  };

  const handleOnSelectedVehicle = ({
    item,
    selected,
  }: {
    item: Vehicle;
    selected: boolean;
  }) => {
    if (!selected) {
      setFilterUiState({ ...filterUiState, vehicle: undefined });
      return;
    }
    // license plate is potentially not unique cross country borders
    setFilterUiState({ ...filterUiState, vehicle: item });
  };
  const handleApplyFilter = () => {
    setFilter(filterUiState);
    useUiStore.getState().setSheetModal('');
  };
  const handleResetFilter = () => {
    clearFilter();
    setFilterUiState(undefined);
    useUiStore.getState().setSheetModal('');
  };

  return (
    <BottomButtonsLayout
      contentBottom={
        <>
          <Button
            {...testIdentifiers('parking-history-apply-filter')}
            text={t('button.apply')}
            size="l"
            buttonType="secondary"
            onPress={handleApplyFilter}
          />
          <ColumnWithMargins mt={20}>
            <Button
              testID="parking-history-clear-filter"
              text={t('button.clearFilter')}
              size="l"
              buttonType="tertiary"
              onPress={handleResetFilter}
            />
          </ColumnWithMargins>
        </>
      }>
      {showParkingTypeSelection && (
        <StyledWrapper mb={40}>
          <Headline3Styled>
            {t('parkingHistoryFilter.paymentTypes')}
          </Headline3Styled>
          <HorizontalRadioList
            selectedValue={filterUiState?.parkingType}
            listData={parkingTypesFilterOptions}
            overflow={DEFAULT_OVERFLOW}
            onSelected={handleOnSelectedParkingType}
          />
        </StyledWrapper>
      )}

      {showPaymentTypeFilter && (
        <StyledWrapper mb={40}>
          <Headline3Styled>{t('parkingHistoryFilter.payment')}</Headline3Styled>
          <HorizontalRadioList
            selectedValue={filterUiState?.payment}
            listData={paymentFilterOptions}
            overflow={DEFAULT_OVERFLOW}
            onSelected={handleOnSelectedPayment}
          />
        </StyledWrapper>
      )}
      <Headline3Styled>{t('parkingHistoryFilter.cars')}</Headline3Styled>
      {vehicles?.map((vehicle, i) => (
        <VehicleFilterItem
          key={vehicle.licensePlate?.plateText}
          onPress={handleOnSelectedVehicle}
          item={vehicle}
          selected={
            filterUiState?.vehicle?.licensePlate?.plateText ===
            vehicle.licensePlate?.plateText
          }
          indexValue={i}
        />
      ))}
    </BottomButtonsLayout>
  );
};

export default ParkingHistoryFilterModalContent;
